import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Row,
  Col,
  Statistic,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Alert,
  Badge,
  Tooltip,
  Progress,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  StopOutlined,
  EyeOutlined,
  EditOutlined,
  BarChartOutlined,
  QrcodeOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { 
  Batch, 
  SerialNumber, 
  ExpiryAlert, 
  LotSerialDashboard,
  BatchFormData,
  SerialNumberFormData,
  FEFORecommendation 
} from '../../types/lotSerial';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

interface LotSerialTrackingProps {
  companyId: string;
}

export const LotSerialTracking: React.FC<LotSerialTrackingProps> = ({ companyId }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState<LotSerialDashboard | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [serialNumbers, setSerialNumbers] = useState<SerialNumber[]>([]);
  const [expiryAlerts, setExpiryAlerts] = useState<ExpiryAlert[]>([]);
  const [fefoRecommendation, setFefoRecommendation] = useState<FEFORecommendation | null>(null);
  
  // Modal states
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [serialModalVisible, setSerialModalVisible] = useState(false);
  const [fefoModalVisible, setFefoModalVisible] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [selectedSerial, setSelectedSerial] = useState<SerialNumber | null>(null);
  
  // Forms
  const [batchForm] = Form.useForm();
  const [serialForm] = Form.useForm();
  const [fefoForm] = Form.useForm();

  useEffect(() => {
    loadDashboard();
  }, [companyId]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      
      // Simulate API call - replace with actual API
      const mockDashboard: LotSerialDashboard = {
        overview: {
          totalBatches: 156,
          totalQuantity: 45230,
          availableQuantity: 42180,
          utilizationRate: 93
        },
        qualityStatus: {
          normal: 142,
          quarantine: 8,
          expired: 4,
          released: 2
        },
        expiryAlerts: {
          total: 23,
          expired: 4,
          expiringSoon: 7,
          nearExpiry: 12,
          alerts: []
        },
        recentBatches: []
      };
      
      setDashboard(mockDashboard);
    } catch (error) {
      message.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async () => {
    try {
      setLoading(true);
      // Simulate API call
      const mockBatches: Batch[] = [
        {
          id: '1',
          batchNumber: 'BATCH-2024-001',
          productId: 'prod1',
          warehouseId: 'wh1',
          manufacturingDate: new Date('2024-01-15'),
          expiryDate: new Date('2024-07-15'),
          arrivalDate: new Date('2024-01-20'),
          qualityStatus: 'NORMAL',
          totalQuantity: 1000,
          availableQuantity: 850,
          reservedQuantity: 150,
          isActive: true,
          companyId,
          createdAt: new Date('2024-01-20'),
          updatedAt: new Date('2024-01-20'),
          product: {
            itemCode: 'ITEM001',
            itemName: 'Sample Product'
          },
          warehouse: {
            name: 'Main Warehouse',
            location: 'Building A'
          }
        }
      ];
      setBatches(mockBatches);
    } catch (error) {
      message.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBatch = async (values: BatchFormData) => {
    try {
      // Simulate API call
      message.success('Batch created successfully');
      setBatchModalVisible(false);
      batchForm.resetFields();
      loadBatches();
    } catch (error) {
      message.error('Failed to create batch');
    }
  };

  const handleCreateSerialNumber = async (values: SerialNumberFormData) => {
    try {
      // Simulate API call
      message.success('Serial number created successfully');
      setSerialModalVisible(false);
      serialForm.resetFields();
    } catch (error) {
      message.error('Failed to create serial number');
    }
  };

  const handleFEFORecommendation = async (values: any) => {
    try {
      setLoading(true);
      
      // Simulate FEFO recommendation
      const mockRecommendation: FEFORecommendation = {
        productId: values.productId,
        warehouseId: values.warehouseId,
        requiredQuantity: values.quantity,
        allocation: [
          {
            batchId: '1',
            batchNumber: 'BATCH-2024-001',
            availableQuantity: 850,
            allocatedQuantity: Math.min(850, values.quantity),
            expiryDate: new Date('2024-07-15'),
            daysToExpiry: 45,
            priority: 'NORMAL',
            recommendation: 'Normal usage'
          }
        ],
        totalAllocated: Math.min(850, values.quantity),
        shortfall: Math.max(0, values.quantity - 850),
        fefoOptimized: true,
        recommendations: [
          {
            type: 'INFO',
            message: 'Allocation optimized using FEFO (First Expired, First Out) logic',
            batches: ['BATCH-2024-001']
          }
        ]
      };
      
      setFefoRecommendation(mockRecommendation);
      setFefoModalVisible(true);
    } catch (error) {
      message.error('Failed to get FEFO recommendation');
    } finally {
      setLoading(false);
    }
  };

  const getQualityStatusColor = (status: string) => {
    const colors = {
      NORMAL: 'green',
      QUARANTINE: 'orange',
      EXPIRED: 'red',
      DAMAGED: 'red',
      RELEASED: 'blue'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getExpiryAlertColor = (alertType: string) => {
    const colors = {
      EXPIRED: 'red',
      EXPIRING_SOON: 'orange',
      NEAR_EXPIRY: 'yellow'
    };
    return colors[alertType as keyof typeof colors] || 'default';
  };

  const batchColumns: ColumnsType<Batch> = [
    {
      title: 'Batch Number',
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      render: (text, record) => (
        <Space>
          <Text strong>{text}</Text>
          {record.expiryDate && (
            <Tag color={moment(record.expiryDate).diff(moment(), 'days') <= 7 ? 'red' : 'green'}>
              {moment(record.expiryDate).diff(moment(), 'days')} days
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Product',
      key: 'product',
      render: (record) => (
        <div>
          <Text strong>{record.product?.itemCode}</Text>
          <br />
          <Text type="secondary">{record.product?.itemName}</Text>
        </div>
      )
    },
    {
      title: 'Quantity',
      key: 'quantity',
      render: (record) => (
        <div>
          <Text>Available: <Text strong>{record.availableQuantity}</Text></Text>
          <br />
          <Text type="secondary">Total: {record.totalQuantity}</Text>
          <br />
          <Progress 
            percent={Math.round((record.availableQuantity / record.totalQuantity) * 100)}
            size="small"
            status={record.availableQuantity < record.totalQuantity * 0.2 ? 'exception' : 'normal'}
          />
        </div>
      )
    },
    {
      title: 'Quality Status',
      dataIndex: 'qualityStatus',
      key: 'qualityStatus',
      render: (status) => (
        <Tag color={getQualityStatusColor(status)}>{status}</Tag>
      )
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      render: (date) => date ? moment(date).format('YYYY-MM-DD') : 'N/A'
    },
    {
      title: 'Warehouse',
      key: 'warehouse',
      render: (record) => record.warehouse?.name || 'N/A'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => setSelectedBatch(record)}>
            View
          </Button>
          <Button type="link" icon={<EditOutlined />}>
            Edit
          </Button>
          <Button type="link" icon={<HistoryOutlined />}>
            History
          </Button>
        </Space>
      )
    }
  ];

  const renderDashboard = () => (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Batches"
              value={dashboard?.overview.totalBatches || 0}
              prefix={<QrcodeOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Quantity"
              value={dashboard?.overview.totalQuantity || 0}
              suffix="units"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Available Quantity"
              value={dashboard?.overview.availableQuantity || 0}
              suffix="units"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Utilization Rate"
              value={dashboard?.overview.utilizationRate || 0}
              suffix="%"
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card title="Quality Status Distribution" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Normal"
                  value={dashboard?.qualityStatus.normal || 0}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Quarantine"
                  value={dashboard?.qualityStatus.quarantine || 0}
                  valueStyle={{ color: '#faad14' }}
                  prefix={<ExclamationCircleOutlined />}
                />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Statistic
                  title="Expired"
                  value={dashboard?.qualityStatus.expired || 0}
                  valueStyle={{ color: '#f5222d' }}
                  prefix={<StopOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Released"
                  value={dashboard?.qualityStatus.released || 0}
                  valueStyle={{ color: '#1890ff' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Expiry Alerts" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Expired"
                  value={dashboard?.expiryAlerts.expired || 0}
                  valueStyle={{ color: '#f5222d' }}
                  prefix={<StopOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Expiring Soon"
                  value={dashboard?.expiryAlerts.expiringSoon || 0}
                  valueStyle={{ color: '#faad14' }}
                  prefix={<WarningOutlined />}
                />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Statistic
                  title="Near Expiry (30 days)"
                  value={dashboard?.expiryAlerts.nearExpiry || 0}
                  valueStyle={{ color: '#fa8c16' }}
                  prefix={<ClockCircleOutlined />}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {dashboard?.expiryAlerts && dashboard.expiryAlerts.total > 0 && (
        <Alert
          message="Expiry Alerts"
          description={`You have ${dashboard.expiryAlerts.total} items requiring attention. ${dashboard.expiryAlerts.expired} expired, ${dashboard.expiryAlerts.expiringSoon} expiring soon.`}
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          action={
            <Button size="small" type="primary" onClick={() => setActiveTab('alerts')}>
              View Alerts
            </Button>
          }
        />
      )}
    </div>
  );

  const renderBatches = () => (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>Batch Management</Title>
        <Space>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setBatchModalVisible(true)}
          >
            Create Batch
          </Button>
          <Button 
            icon={<BarChartOutlined />}
            onClick={() => fefoForm.resetFields() || setFefoModalVisible(true)}
          >
            FEFO Recommendation
          </Button>
        </Space>
      </div>
      
      <Table
        columns={batchColumns}
        dataSource={batches}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />
    </div>
  );

  return (
    <Card title="Lot/Serial Tracking & Expiry Management" style={{ margin: '16px 0' }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={<span><BarChartOutlined />Dashboard</span>} key="dashboard">
          {renderDashboard()}
        </TabPane>
        <TabPane tab={<span><QrcodeOutlined />Batches</span>} key="batches">
          {renderBatches()}
        </TabPane>
        <TabPane tab="Serial Numbers" key="serials">
          <div>Serial Numbers Management (Coming Soon)</div>
        </TabPane>
        <TabPane tab={<Badge count={dashboard?.expiryAlerts.total || 0}><span><WarningOutlined />Expiry Alerts</span></Badge>} key="alerts">
          <div>Expiry Alerts Management (Coming Soon)</div>
        </TabPane>
      </Tabs>

      {/* Create Batch Modal */}
      <Modal
        title="Create New Batch"
        open={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form form={batchForm} layout="vertical" onFinish={handleCreateBatch}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="batchNumber" label="Batch Number" rules={[{ required: true }]}>
                <Input placeholder="Enter batch number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="productId" label="Product" rules={[{ required: true }]}>
                <Select placeholder="Select product">
                  <Option value="prod1">Sample Product</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="warehouseId" label="Warehouse" rules={[{ required: true }]}>
                <Select placeholder="Select warehouse">
                  <Option value="wh1">Main Warehouse</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="totalQuantity" label="Total Quantity" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="manufacturingDate" label="Manufacturing Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expiryDate" label="Expiry Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="arrivalDate" label="Arrival Date">
                <DatePicker style={{ width: '100%' }} defaultValue={moment()} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="qualityStatus" label="Quality Status" initialValue="NORMAL">
                <Select>
                  <Option value="NORMAL">Normal</Option>
                  <Option value="QUARANTINE">Quarantine</Option>
                  <Option value="RELEASED">Released</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplierBatchId" label="Supplier Batch ID">
                <Input placeholder="Enter supplier batch ID" />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setBatchModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Create Batch</Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* FEFO Recommendation Modal */}
      <Modal
        title="FEFO (First Expired, First Out) Recommendation"
        open={fefoModalVisible}
        onCancel={() => setFefoModalVisible(false)}
        footer={null}
        width={800}
      >
        {!fefoRecommendation ? (
          <Form form={fefoForm} layout="vertical" onFinish={handleFEFORecommendation}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="productId" label="Product" rules={[{ required: true }]}>
                  <Select placeholder="Select product">
                    <Option value="prod1">Sample Product</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="warehouseId" label="Warehouse" rules={[{ required: true }]}>
                  <Select placeholder="Select warehouse">
                    <Option value="wh1">Main Warehouse</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="quantity" label="Required Quantity" rules={[{ required: true }]}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setFefoModalVisible(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Get Recommendation
                </Button>
              </Space>
            </div>
          </Form>
        ) : (
          <div>
            <Alert
              message="FEFO Optimization Complete"
              description={`Allocated ${fefoRecommendation.totalAllocated} units using First Expired, First Out logic.`}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            {fefoRecommendation.recommendations.map((rec, index) => (
              <Alert
                key={index}
                message={rec.message}
                type={rec.type === 'URGENT' ? 'error' : rec.type === 'WARNING' ? 'warning' : 'info'}
                showIcon
                style={{ marginBottom: 8 }}
              />
            ))}
            
            <Divider>Batch Allocation</Divider>
            
            {fefoRecommendation.allocation.map((allocation, index) => (
              <Card key={index} size="small" style={{ marginBottom: 8 }}>
                <Row>
                  <Col span={8}>
                    <Text strong>{allocation.batchNumber}</Text>
                    <br />
                    <Tag color={allocation.priority === 'HIGH' ? 'red' : 'green'}>
                      {allocation.priority}
                    </Tag>
                  </Col>
                  <Col span={8}>
                    <Text>Allocated: <Text strong>{allocation.allocatedQuantity}</Text></Text>
                    <br />
                    <Text type="secondary">Available: {allocation.availableQuantity}</Text>
                  </Col>
                  <Col span={8}>
                    <Text>Expiry: {allocation.expiryDate ? moment(allocation.expiryDate).format('YYYY-MM-DD') : 'N/A'}</Text>
                    <br />
                    <Text type="secondary">{allocation.daysToExpiry} days remaining</Text>
                  </Col>
                </Row>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{allocation.recommendation}</Text>
                </div>
              </Card>
            ))}
            
            {fefoRecommendation.shortfall > 0 && (
              <Alert
                message={`Shortfall: ${fefoRecommendation.shortfall} units`}
                description="Additional stock needed to fulfill the complete requirement."
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
            
            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <Space>
                <Button onClick={() => {
                  setFefoRecommendation(null);
                  setFefoModalVisible(false);
                }}>
                  Close
                </Button>
                <Button type="primary">Apply Allocation</Button>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default LotSerialTracking; 